import React, { useEffect, useState } from 'react';
import { CarryOutOutlined } from '@ant-design/icons';
import { Empty, Tree } from 'antd';
import type { TreeDataNode } from 'antd';
import { Course, Curriculum, Subject } from '../../../../types/dataModel';
import AcademicNodeDataModal from './academicNodeDataModal';
import moment from 'moment';
import { toDateFromEpoch } from '../../../../common/Utils';


const DEFAULT_TITLE = "No Data To View !!"
const NODE_TYPE_CURRICULUM = 'CURRICULUM'
const NODE_TYPE_COURSE = 'COURSE'
const NODE_TYPE_SUBJECT = 'SUBJECT'

interface Props {
  data: any[] 
  refresh: ()=>void
  courseOptions: any[]
  subjectOptions: any[]
  syllabusOptions: any[]
}

const AcademicTreeView = ({data, refresh, courseOptions, subjectOptions, syllabusOptions}:Props) => {

  const [liveTreeData, setLiveTreeData] = useState<TreeDataNode[]>([])
  const [activeRecord, setActiveRecord] = useState<any>();
  const [expandedKeys, setExpandedKeys] = useState<any[]>([]);

  const expandAll = () => {
    const allKeys = liveTreeData?.flatMap(getAllKeys);
    setExpandedKeys(allKeys);
  };

  // Function to collapse all nodes
  const collapseAll = () => {
    setExpandedKeys([]);
  };

  // Function to extract all keys from treeData
  const getAllKeys = (node:any) => {
    let keys = [node.key];
    if (node.children) {
      keys = keys.concat(node.children.flatMap(getAllKeys));
    }
    return keys;
  };

  const onExpand = (newExpandedKeys:any[]) => {
    setExpandedKeys(newExpandedKeys);
  };

  const findNode = (treeData: TreeDataNode[], record: any): any | null => {
    if(record){
      for (const node of treeData) {
        let item:any = node?.title
        if (item.id === record.id && 
            item.code === record.code && 
            item.nodeType === record.nodeType &&
            item.live === record.live) {
          return item; // Found the node
        }
        if (node.children) {
          const foundNode = findNode(node.children, record);
          if (foundNode) return foundNode; // Found in children
        }
      }
      return null; // Not found
    }
  };


  useEffect(()=>{
    if(!data?.length){
      setLiveTreeData([{ title: DEFAULT_TITLE, key: `${0}`, icon: <CarryOutOutlined />, children:[] }])
    } else {
      let tempTree = parseToTreeData(data)
      setLiveTreeData(tempTree)
      setActiveRecord(findNode(tempTree, activeRecord))
    }
  },[data])

  const renderTitle = (record: any) => {
    let treeNodeAcademicData = record.title;
    return (
      <div style={{ flexDirection: 'row' }}>
        {record?.title === DEFAULT_TITLE ? (
          <>
            <i className="bg-dark br-5 p-1 fs-16">{treeNodeAcademicData?.title}</i>
          </>
        ) : treeNodeAcademicData?.nodeType === NODE_TYPE_CURRICULUM ? (
          <>
            <button
              data-bs-toggle="offcanvas"
              data-bs-target="#node_modal"
              className={`btn btn-sm btn-outline-dark br-5 p-1 fs-12 d-inline-md-block`}
              onClick={() => {
                setActiveRecord(treeNodeAcademicData);
              }}
            >
              {treeNodeAcademicData?.title}
            </button>
            <span className="text-dark ms-2 fs-10">{treeNodeAcademicData?.code}</span>
            <span
              className={`text-${
                treeNodeAcademicData?.live === 'ACTIVE'
                  ? 'success'
                  : treeNodeAcademicData?.live === 'UPCOMING'
                  ? 'info'
                  : 'danger'
              } ms-2 fs-10`}
            >
              {treeNodeAcademicData?.live === 'ACTIVE'
                ? 'ACTIVE'
                : treeNodeAcademicData?.live === 'UPCOMING'
                ? 'UPCOMING'
                : 'INACTIVE'}
            </span>
          </>
        ) : treeNodeAcademicData?.nodeType === NODE_TYPE_COURSE ? (
          <>
            <button
              data-bs-toggle="offcanvas"
              data-bs-target="#node_modal"
              className="btn btn-sm btn-outline-secondary br-5 p-1 fs-12 d-inline-md-block"
              onClick={() => {
                setActiveRecord(treeNodeAcademicData);
              }}
            >
              {treeNodeAcademicData?.title}
            </button>
            <span className="text-secondary ms-2 fs-10">{treeNodeAcademicData?.code}</span>
          </>
        ) : treeNodeAcademicData?.nodeType === NODE_TYPE_SUBJECT ? (
          <>
            <button
              data-bs-toggle="offcanvas"
              data-bs-target="#node_modal"
              className="btn btn-sm btn-outline-primary br-5 p-1 fs-12 d-inline-md-block"
              onClick={() => {
                setActiveRecord(treeNodeAcademicData);
              }}
            >
              {treeNodeAcademicData?.title}
            </button>
            <span className="text-primary ms-2 fs-10">{treeNodeAcademicData?.code}</span>
          </>
        ) : (
          <></>
        )}
      </div>
    );
  };
  
  
  const onSelect = (selectedKeys: React.Key[], info: any) => {
    
  };

  const renderNodeDataModal = () =>{
    return (
      <AcademicNodeDataModal refresh={refresh} data={activeRecord} courseOptions={courseOptions} subjectOptions={subjectOptions} syllabusOptions={syllabusOptions}/>
    )     
  }

  return (
    <>
    {renderNodeDataModal()}
    <div className='gap-4'>
      <div className='mb-3 d-flex justify-content-between'>
        <div className='d-flex text-dark fs-18 text-center flex-row align-items-center'>
           <i className='ti ti-book me-2'/>
           <h4 style={{textDecoration:'underline'}}>Curriculum</h4>
        </div>
        <div>
        <button className='btn btn-xs btn-outline-dark me-2'
         onClick={expandAll}>
          <i className='ti ti-arrow-down' /> Expand All
        </button>
        <button className='btn btn-xs btn-outline-dark' onClick={collapseAll}>
          <i className='ti ti-arrow-up' /> Collapse All
        </button>
        </div>

      </div>
      {
        liveTreeData?.length
        ?
        <>
          <div className="d-flex">
            <Tree
              showLine={true}
              showIcon={false}
              onSelect={onSelect}
              treeData={liveTreeData}
              defaultExpandAll={liveTreeData?.length? true : false}
              titleRender={renderTitle}
              expandedKeys={expandedKeys}
              onExpand={onExpand}
            />
            
          </div>
        </>
        :
        <>
          <Empty/>
        </>
      }
    </div>
    </>
  );
};

const getNextKey = (parentKey: string, index: number|undefined) => {
   return `${parentKey}-${index}`
}

const parseToTreeData = ( currs: Curriculum[]| undefined): TreeDataNode[] => {
  let nodes: TreeDataNode[] = []
  if(currs?.length){
    let sortedCurr = currs?.sort((a:Curriculum , b:Curriculum ) => ( a.title && b.title ? a.title?.localeCompare(b.title) : 0))
    sortedCurr.forEach((curr:Curriculum, curInd:number)=>{
      let status = getStatus(curr.startDate, curr.endDate);
      let newCurrKey = getNextKey('0', curInd)
      let newCurrNode:TreeDataNode = parseToTreeNode({...curr, nodeType: NODE_TYPE_CURRICULUM, live: status}, newCurrKey)
      let sortedCourse = curr?.courses?.sort((a:Course , b:Course ) => ( a.title && b.title ? a.title?.localeCompare(b.title) : 0))
      sortedCourse?.forEach((cr:Course, crsInd:number)=>{
            let newCrsKey = getNextKey(newCurrKey, crsInd)
            let newCrsNode = parseToTreeNode({ ...cr, nodeType: NODE_TYPE_COURSE, live: status} , newCrsKey) 
            let sortedSubjects = cr?.subjects?.sort((a:Subject , b:Subject ) => ( a.title && b.title ? a.title?.localeCompare(b.title) : 0))
            sortedSubjects?.forEach((sub:Subject, subIndx:number)=>{
              let newSubKey = getNextKey(newCrsKey, subIndx)
              let newSubNode = parseToTreeNode({...sub, nodeType: NODE_TYPE_SUBJECT, live: status }, newSubKey)
              newCrsNode.children?.push(newSubNode) 
              })

            newCurrNode?.children?.push(newCrsNode)
          })

          nodes.push(newCurrNode)
          
      })

  }
  return nodes
}

const parseToTreeNode = (item: any, key: string):TreeDataNode => {    
  return { title: item, key: `${key}`, icon: <CarryOutOutlined />, children:[] }
}

const isLive = (startEpochDt?: number, endEpochDt?: number) =>{
  if(startEpochDt && endEpochDt){
    const startDate = moment(toDateFromEpoch(startEpochDt));
    const endDate = moment(toDateFromEpoch(endEpochDt));
    const currentDate = moment();

    if (currentDate.isBetween(startDate, endDate, 'second', '[]')) {
       return true
    } else {
        return false
    }
  } else {
    return false
  }

}

const getStatus = (startEpochDt?: number, endEpochDt?: number): string => {
  if (startEpochDt && endEpochDt) {
    const startDate = moment(toDateFromEpoch(startEpochDt));
    const endDate = moment(toDateFromEpoch(endEpochDt));
    const currentDate = moment();

    if (currentDate.isBetween(startDate, endDate, 'second', '[]')) {
      return 'ACTIVE'; // Current date is between start and end dates
    } else if (currentDate.isBefore(startDate)) {
      return 'UPCOMING'; // Current date is before the start date
    } else {
      return 'INACTIVE'; // Current date is after the end date
    }
  } else {
    return 'UNKNOWN'; // If dates are missing
  }
};


export default AcademicTreeView;